import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { PortMessage } from '../../../types/PortMessage';
import { CategoryState } from '../../../types/ReachInformationMessage';
import { PaginatedAsyncComboBox } from '../../cdl/ComboBox/ComboBox';
import type { PaginatedAsyncComboBoxProps } from '../../cdl/ComboBox/ComboBox';
import { getLatestPorts } from '../../common/api/clients/gateway.api';
import { getPorts } from '../../common/api/clients/port.api';
import { queries } from '../../common/api/queryKeys/queries';
import { formatPort } from '../../common/helpers/formatPort.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useKeyPorts } from '../../common/hooks/useKeyPorts';
import { useRole } from '../../common/hooks/useRole';
import type { Schedule } from '../../common/hooks/useSchedules';
import { IconInfoCircle } from '../../common/icons/cdl/InfoCircle';

import { PortReachSeparator } from './components/PortReachSeparator';
import { PortSelectOption } from './components/PortSelectOption';
import { PortSingleValue } from './components/PortSingleValue';
import { PortOptionType } from './PortOptionType';
import { getKeyPortIds, toNoScheduleOptions, toScheduleOptions, toSearchOptions } from './utils/portOptionMappers';

export interface NoSchedulePortOption {
    type: PortOptionType.NO_SCHEDULE;
    label: string;
    value: PortMessage['id'];
    port: PortMessage;
    vesselId?: string;
}

export interface SchedulePortOption {
    type: PortOptionType.SCHEDULE;
    label: string;
    value: Schedule['id'];
    port: PortMessage;
    vesselId?: string;
    scheduleEntry: Schedule;
    schedulesLength: number;
    showReachSeparator: boolean;
}

export type PortOption = NoSchedulePortOption | SchedulePortOption;

export interface PortDetailSelectProps
    extends Omit<
        PaginatedAsyncComboBoxProps<PortOption>,
        'onChange' | 'value' | 'optionsQueryKey' | 'optionsQueryFn' | 'selectedOptionQueryKey' | 'selectedOptionQueryFn'
    > {
    value: string;
    onChange: (option: PortOption | null, scheduleRank?: number) => void;
    schedule: Schedule[];
    vesselId?: string;
}

export const PortDetailSelect = ({
    value,
    onChange,
    schedule,
    label = translate('portSelect.label'),
    vesselId,
    ...rest
}: PortDetailSelectProps) => {
    const { keyPorts } = useKeyPorts();
    const queryClient = useQueryClient();
    const role = useRole();

    // Past enquiry and key ports are customer-private — never expose them to suppliers.
    const showCustomerPorts = role.isCustomer();

    const labelInfo =
        rest.labelInfo ??
        (role.isSupplier() && schedule.length > 0 ? translate('portSelect.scheduleSharing.tooltip') : undefined);
    const optionsCacheKey = JSON.stringify([
        vesselId ?? '',
        getKeyPortIds(keyPorts),
        schedule.map((entry) => [
            entry.id,
            entry.port?.id ?? '',
            entry.port?.name ?? '',
            entry.port?.country.name ?? '',
            entry.port?.locCode ?? '',
            entry.eta,
            entry.timeZoneId,
            entry.reachStatus ?? '',
            entry.rank,
        ]),
    ]);

    const loadOptions = async (searchQuery: string, page: number) => {
        if (searchQuery) {
            const portsPage = await getPorts({ searchQuery, page });

            return {
                ...portsPage,
                content: toSearchOptions(portsPage.content, schedule, vesselId),
            };
        }

        if (page > 0) {
            const remainingPortsPage = await getPorts({
                page,
                sortOptions: { key: 'NAME', direction: 'ASC' },
            });
            const schedulePortIds = new Set(
                schedule.flatMap((scheduleEntry) => (scheduleEntry.port ? [scheduleEntry.port.id] : []))
            );

            return {
                ...remainingPortsPage,
                content: toNoScheduleOptions(
                    remainingPortsPage.content.filter((port) => !schedulePortIds.has(port.id)),
                    vesselId
                ),
            };
        }

        const seen = new Set<string>();
        const onlyUnseen = (options: PortOption[]) => {
            const unseen = options.filter((option) => !seen.has(option.value));
            unseen.forEach((option) => seen.add(option.value));
            return unseen;
        };

        const keyPortIds = showCustomerPorts ? getKeyPortIds(keyPorts, vesselId) : [];

        const [latestPorts, keyPortPorts, remainingPortsPage] = await Promise.all([
            showCustomerPorts ? (getLatestPorts() as Promise<PortMessage[]>) : Promise.resolve<PortMessage[]>([]),
            keyPortIds.length
                ? getPorts({ ids: keyPortIds }).then((response) => response.content)
                : Promise.resolve([]),
            getPorts({ page: 0, sortOptions: { key: 'NAME', direction: 'ASC' } }),
        ]);

        const scheduleOptions = toScheduleOptions(schedule, vesselId);
        scheduleOptions.forEach((option) => seen.add(option.port.id));

        return {
            ...remainingPortsPage,
            content: [
                ...scheduleOptions,
                ...onlyUnseen(toNoScheduleOptions(latestPorts, vesselId)),
                ...onlyUnseen(toNoScheduleOptions(keyPortPorts, vesselId)),
                ...onlyUnseen(toNoScheduleOptions(remainingPortsPage.content, vesselId)),
            ],
        };
    };

    const loadPortById = useCallback(
        async (portId: string): Promise<PortOption> => {
            const matchingScheduleEntry = schedule.find((entry) => entry.port?.id === portId);
            const matchingScheduleOption = matchingScheduleEntry
                ? toScheduleOptions(schedule, vesselId).find(
                      (option) =>
                          option.type === PortOptionType.SCHEDULE &&
                          option.scheduleEntry.id === matchingScheduleEntry.id
                  )
                : undefined;
            if (matchingScheduleOption) {
                return matchingScheduleOption;
            }

            const response = await getPorts({ ids: [portId] });
            const port = response.content[0];
            return {
                type: PortOptionType.NO_SCHEDULE,
                label: formatPort({ port }),
                value: port.id,
                port,
                vesselId,
            };
        },
        [schedule, vesselId]
    );

    const loadPortBySelectionValue = async (selectionValue: string): Promise<PortOption> => {
        const matchingScheduleOption = toScheduleOptions(schedule, vesselId).find(
            (option) => option.value === selectionValue
        );

        return matchingScheduleOption ?? loadPortById(selectionValue);
    };

    const selectedValueQuery = useQuery({
        queryKey: queries.ports.selectedDetailComboBoxOption(value, optionsCacheKey).queryKey,
        queryFn: async () => [await loadPortById(value)],
        enabled: !!value,
        staleTime: Infinity,
    });
    const selectedValue = value ? selectedValueQuery.data?.[0] ?? null : null;

    const updateSelectedValue = (option: PortOption | null) => {
        if (option) {
            queryClient.setQueryData(
                queries.ports.selectedDetailComboBoxOption(option.port.id, optionsCacheKey).queryKey,
                [option]
            );
        }

        if (option?.type === PortOptionType.SCHEDULE) {
            onChange(option, option.scheduleEntry.rank);
        } else {
            onChange(option);
        }
    };

    const selectedPortIsOutOfReach =
        selectedValue?.type === PortOptionType.SCHEDULE &&
        selectedValue.scheduleEntry.reachStatus === CategoryState.OUT_OF_REACH;
    const selectedPortIsInSchedule = schedule.some((scheduleEntry) => scheduleEntry.port?.id === value);
    const showSelectedOptionAtTop = rest.showSelectedOptionAtTop !== false && !selectedPortIsInSchedule;
    const caption =
        rest.caption || (selectedPortIsOutOfReach ? translate('portSelect.reach.outOfReachHelper') : undefined);
    const CaptionIcon = !rest.caption && selectedPortIsOutOfReach ? IconInfoCircle : rest.CaptionIcon;

    return (
        <PaginatedAsyncComboBox<PortOption>
            key={vesselId}
            {...rest}
            caption={caption}
            CaptionIcon={CaptionIcon}
            label={label}
            labelInfo={labelInfo}
            onChange={(_, meta) => updateSelectedValue(meta.option)}
            value={(selectedValue?.value ?? value) || null}
            optionsQueryKey={(search) =>
                queries.ports.detailComboBoxOptions({ cacheKey: optionsCacheKey, searchQuery: search }).queryKey
            }
            optionsQueryFn={({ search, page }) => loadOptions(search, page)}
            selectedOptionQueryKey={(selectionValue) =>
                queries.ports.selectedDetailComboBoxOption(selectionValue, optionsCacheKey).queryKey
            }
            selectedOptionQueryFn={loadPortBySelectionValue}
            showSelectedOptionAtTop={showSelectedOptionAtTop}
            OptionComponent={PortSelectOption}
            OptionSeparatorComponent={PortReachSeparator}
            ValueComponent={PortSingleValue}
        />
    );
};
